<script lang="ts">
	// @ts-nocheck
	import TripadvisorAward from '$lib/components/TripadvisorAward.svelte';
	import { onMount } from 'svelte';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import * as Select from '$lib/components/ui/select';
	import { Textarea } from '$lib/components/ui/textarea';
	import * as Alert from '$lib/components/ui/alert';
	import * as Carousel from '$lib/components/ui/carousel';
	import * as Card from '$lib/components/ui/card';
	import * as Tabs from '$lib/components/ui/tabs';
	import { Badge } from '$lib/components/ui/badge';
	import { fly, slide } from 'svelte/transition';
	import {
		Check,
		CheckCircle,
		Loader2,
		Star,
		MapPin,
		Plane,
		Leaf,
		Bird
	} from 'lucide-svelte';
	import Seo from '$lib/components/SEO.svelte';
	import allTours from '$lib/data/allTours.json';
	import TourCard from '$lib/components/tours/TourCard.svelte';

	// Hero
	import heroImage from '$lib/assets/images/hero-carousel-1.jpg?enhanced';

	// Destination images
	import hobartImage from '$lib/assets/enhanced/hobart-highlights.jpg?enhanced';
	import brunyDestImage from '$lib/assets/highlights/bruny-island-1.jpg?enhanced';
	import portArthurImage from '$lib/assets/enhanced/port-arthur-shuttle.jpg?enhanced';
	import wineglassImage from '$lib/assets/enhanced/wineglass-bay.jpg?enhanced';
	import cradleImage from '$lib/assets/enhanced/cradle-mountain.jpg?enhanced';

	// Illustrations
	import mountainIllustration from '$lib/assets/illustrations/mountain.png?enhanced';
	import adventureIllustration from '$lib/assets/illustrations/adventure.png?enhanced';
	import mapIllustration from '$lib/assets/illustrations/map.png?enhanced';
	import certIllustration from '$lib/assets/illustrations/certificate.png?enhanced';
	import foodIllustration from '$lib/assets/illustrations/food.png?enhanced';
	import restingIllustration from '$lib/assets/illustrations/resting.png?enhanced';

	const videoUrl = 'https://ik.imagekit.io/codec/Tassie%20Tours/hero-video.mp4';
	const startTime = 2;
	const endTime = 27;
	let videoElement;

	let animate = $state(false);
	let privateExpanded = $state(false);

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
		animate = true;

		if (videoElement) {
			videoElement.currentTime = startTime;
			videoElement.addEventListener('timeupdate', () => {
				if (videoElement.currentTime >= endTime) {
					videoElement.currentTime = startTime;
				}
			});
		}
	});

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		if (anchor) {
			window.scrollTo({ top: anchor.offsetTop, behavior: 'smooth' });
		}
	}

	// Form state
	let submitted = $state(false);
	let loading = $state(false);
	let firstName = $state('');
	let lastName = $state('');
	let email = $state('');
	let country = $state('');
	let groupSize = $state('');
	let interests = $state('');
	let type = 'SE Asia Enquiry';

	const countryOptions = [
		{ value: 'singapore', label: 'Singapore' },
		{ value: 'malaysia', label: 'Malaysia' },
		{ value: 'thailand', label: 'Thailand' },
		{ value: 'indonesia', label: 'Indonesia' },
		{ value: 'philippines', label: 'Philippines' },
		{ value: 'vietnam', label: 'Vietnam' },
		{ value: 'hong-kong', label: 'Hong Kong' },
		{ value: 'other', label: 'Other' }
	];

	const groupSizeOptions = [
		{ value: 'solo', label: 'Solo' },
		{ value: '2', label: '2 people' },
		{ value: '3-5', label: '3–5 people' },
		{ value: '6-10', label: '6–10 people' },
		{ value: '10+', label: '10+ people' }
	];

	const countryTrigger = $derived(
		countryOptions.find((o) => o.value === country)?.label ?? 'Select your country'
	);
	const groupSizeTrigger = $derived(
		groupSizeOptions.find((o) => o.value === groupSize)?.label ?? 'Select group size'
	);

	function resetForm() {
		firstName = '';
		lastName = '';
		email = '';
		country = '';
		groupSize = '';
		interests = '';
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		loading = true;
		const formData = {
			name: `${firstName} ${lastName}`,
			email,
			country,
			groupSize,
			interests,
			type
		};
		try {
			const response = await fetch('/email/send-email', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(formData)
			});
			if (!response.ok) throw new Error('Failed to send email');
			const result = await response.json();
			if (result.success) {
				resetForm();
				submitted = true;
			} else {
				throw new Error(result.error || 'Failed to send email');
			}
		} catch (error) {
			console.error('Error sending email:', error);
		} finally {
			loading = false;
		}
	}

	const whyChooseItems = [
		{
			title: 'Educated, passionate guides',
			body: `Our guides are educated, passionate Tasmanians and excellent storytellers — bringing the island's history, ecology, and hidden gems to life on every tour.`,
			illustration: 'mountain'
		},
		{
			title: 'Small groups',
			body: "Most tours have 10–20 people maximum. You're not in a bus with 50 strangers — you're on a real guided experience.",
			illustration: 'adventure'
		},
		{
			title: 'Wide range of routes',
			body: 'Day tours, multi-day packages, private tours, shore excursions — we cover every part of the island with routes to match.',
			illustration: 'map'
		},
		{
			title: 'Award-recognised',
			body: "Silver Award winners at the Tasmanian Tourism Awards. TripAdvisor Travellers' Choice Award winner. Quality Tourism Accredited. Over 2,000 five-star reviews from visitors around the world.",
			illustration: 'certificate'
		},
		{
			title: 'Tailored to your interests',
			body: `We design tours to suit each customer's needs and special interests. Tell us what matters most and we'll build the experience around you.`,
			illustration: 'resting'
		},
		{
			title: 'Modern fleet, trusted suppliers',
			body: 'Travel in comfort in our new, modern vehicles. We work with suppliers across the entire market to ensure smooth operations and competitive prices.',
			illustration: 'food'
		}
	];

	const validTours = Array.isArray(allTours) ? allTours : [];

	const dayTourCodes = [
		'P18ZC5',
		'PJHLE0',
		'PNVP8M',
		'P01GQC',
		'PQ5CJC',
		'PP0SGV',
		'PQKW4C',
		'PMPLBM',
		'PTC0KJ',
		'PNNK81',
		'PCBKLS',
		'PHJFAH'
	];

	const multiDayTourCodes = [
		'PU9VRB',
		'P6GE12',
		'P1AHF0',
		'P0QKC2',
		'PHKZNS',
		'PKQPM9',
		'PTNCNK',
		'PM5WCG',
		'PBRPKL',
		'PVHGUQ',
		'P0V9R2',
		'PTWH1T'
	];

	const privateTourCodes = [
		'P80WBP',
		'PUYDUT',
		'PMFU3K',
		'PVSUAB',
		'P0QZS7',
		'PXA0PL',
		'PKFPUC',
		'P9AM50',
		'PLQWG9'
	];

	function getOrderedTours(codes, productType) {
		return codes
			.map((code) => {
				const tour = validTours.find((t) => t.productCode === code);
				return tour ? { ...tour, productType } : null;
			})
			.filter(Boolean);
	}

	const allDayTours = validTours.filter(
		(tour) => tour.productType === 'DAYTOUR' || tour.productType === 'DAY TOUR'
	);
	const allMultiDayTours = validTours.filter(
		(tour) => tour.productType === 'MULTIDAYTOUR' || tour.productType === 'MULTI DAY TOUR'
	);
	const allPrivateTours = validTours.filter(
		(tour) => tour.productType === 'PRIVATETOUR' || tour.productType === 'PRIVATE TOUR'
	);

	const sortedDayTours =
		getOrderedTours(dayTourCodes, 'DAYTOUR').length > 0
			? getOrderedTours(dayTourCodes, 'DAYTOUR')
			: allDayTours;

	const sortedMultiDayTours =
		getOrderedTours(multiDayTourCodes, 'MULTIDAYTOUR').length > 0
			? getOrderedTours(multiDayTourCodes, 'MULTIDAYTOUR')
			: allMultiDayTours;

	const sortedPrivateTours =
		getOrderedTours(privateTourCodes, 'PRIVATETOUR').length > 0
			? getOrderedTours(privateTourCodes, 'PRIVATETOUR')
			: allPrivateTours;

	const destinations = [
		{
			title: 'Wineglass Bay',
			subtitle: 'Freycinet National Park',
			region: 'East Tasmania',
			image: 'wineglass'
		},
		{
			title: 'Bruny Island',
			subtitle: 'South of Hobart',
			region: 'South Tasmania',
			image: 'bruny'
		},
		{
			title: 'Cradle Mountain',
			subtitle: 'UNESCO Wilderness',
			region: 'North West',
			image: 'cradle'
		},
		{
			title: 'Hobart',
			subtitle: "Tasmania's capital city",
			region: 'Capital City',
			image: 'hobart'
		}
	];

	const reviews = [
		{
			name: 'Marilyn P',
			tour: 'Bruny Island Day Tour',
			title: 'This was one of the highlights of our three-week vacation',
			body: 'Our host Nathan was friendly, funny and knowledgeable. The beaches are spectacular, the rainforest exquisite.'
		},
		{
			name: 'Carl L',
			tour: 'Port Arthur Day Tour',
			title: 'Arrived in Hobart on a cruise and stepped off the ship to find this company',
			body: 'Super simple, great advice. Our guide was amazing — we are definitely inspired to come back.'
		},
		{
			name: 'Lottie S',
			tour: 'Bruny Island Day Tour',
			title: 'Absolutely fantastic trip to Bruny Island',
			body: "Guide Jodi was so knowledgeable! We will recommend this to all our friends and can't wait to come back and join another tour."
		}
	];

	const cities = [
		'Singapore',
		'Kuala Lumpur',
		'Bangkok',
		'Jakarta',
		'Ho Chi Minh City',
		'Hong Kong'
	];
</script>

<Seo
	title="SE Asia Travellers — Discover Tasmania with Tassie Tours"
	description="For travellers from Southeast Asia discovering Australia, Tasmania is the experience that rewrites the itinerary. Ancient wilderness, world-class food, and landscapes unlike anything else on the continent."
	keywords="Tasmania tours Southeast Asia, Tasmania Singapore, Tasmania Malaysia, Tasmania Thailand, Tasmania travel, Tassie Tours international"
	siteName="Tassie Tours"
	imageURL="/images/hero-carousel-1.jpg"
	logo="/src/lib/assets/logo/logo-black.png"
	author="Tassie Tours"
	type="website"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
/>

<!-- Hero -->
<section
	class="relative mx-auto flex h-[600px] flex-col items-center justify-center overflow-hidden"
>
	<div
		class="absolute bottom-0 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(26% 96%,100% 73%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<div class="absolute inset-0 h-[600px] w-full">
		<video
			bind:this={videoElement}
			autoplay
			muted
			playsinline
			poster={heroImage}
			class="scaled-video h-full w-full object-cover"
		>
			<source src={videoUrl} type="video/mp4" />
		</video>
	</div>
	<div class="absolute inset-0 bg-foreground/55"></div>
	<div
		class={`z-10 mx-auto flex max-w-4xl flex-col items-center justify-center px-4 text-center ${
			animate ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
		}`}
		style="transition: all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s"
	>
		<h1
			class="max-w-4xl font-heading text-4xl font-bold tracking-tight text-background sm:text-5xl md:text-6xl"
		>
			Australia's most wild island awaits
		</h1>
		<div class="mx-auto mt-6 flex max-w-2xl items-center">
			<p class="text-md mx-auto text-center font-semibold text-background lg:text-lg">
				For travellers from Southeast Asia discovering Australia, Tasmania is the experience that
				rewrites the itinerary. Ancient wilderness, world-class food, and landscapes unlike anything
				else on the continent.
			</p>
		</div>
		<div class="mx-auto flex flex-col items-center justify-center gap-4 sm:flex-row">
			<Button
				data-sveltekit-noscroll
				href="/tours"
				class="pointer-events-auto mx-auto mt-8 rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
				>View all tours</Button
			>
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				variant="outline"
				href="#enquiry"
				class="pointer-events-auto mx-auto mt-8 rounded-full border-2 bg-transparent px-6 font-semibold text-background lg:text-lg"
				>Enquire now</Button
			>
		</div>
		<div class="pointer-events-none z-10 mt-6 w-full max-w-xl">
			<div
				class="flex items-start gap-3 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-left text-sm text-white backdrop-blur-sm"
			>
				<span
					class="mt-0.5 shrink-0 rounded-full bg-[#ffd451] px-2 py-0.5 text-xs font-semibold text-foreground"
					>Coming soon</span
				>
				<p>
					Direct flights from SE Asia to Tasmania expected within 12–24 months. Get ahead of the
					rush.
				</p>
			</div>
		</div>
	</div>
</section>

<!-- Why Tasmania -->
<div class="bg-white pt-12">
	<div class="mx-auto max-w-7xl px-4">
		<div class="mx-auto max-w-5xl">
			<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">Why Tasmania</p>
			<h2 class="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				Australia's best-kept secret, no longer a secret
			</h2>
			<p class="mt-6 leading-7 text-gray-700">
				Tasmania sits below the Australian mainland, protected from the crowds and the heat. It has
				some of the cleanest air on Earth, UNESCO World Heritage wilderness, and wildlife found
				nowhere else on the planet.
			</p>
			<p class="mt-4 leading-7 text-gray-700">
				For travellers from Singapore, Malaysia, Thailand, Indonesia, Hong Kong and beyond, Tasmania
				represents something rare — a destination that's genuinely different from anywhere else. And
				with direct flight routes on the horizon, now is the time to explore.
			</p>
		</div>

		<div class="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-8 pb-20 sm:grid-cols-3">
			<div class="flex flex-col gap-3 rounded-2xl bg-secondary p-6">
				<div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#194139]/10">
					<Leaf class="h-6 w-6 text-[#194139]" />
				</div>
				<h3 class="font-heading text-lg font-semibold">UNESCO World Heritage</h3>
				<p class="text-sm leading-6 text-gray-600">
					Over 40% of Tasmania is protected wilderness, a proportion that rivals the world's most
					pristine natural regions.
				</p>
			</div>
			<div class="flex flex-col gap-3 rounded-2xl bg-secondary p-6">
				<div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#194139]/10">
					<Bird class="h-6 w-6 text-[#194139]" />
				</div>
				<h3 class="font-heading text-lg font-semibold">Unique wildlife</h3>
				<p class="text-sm leading-6 text-gray-600">
					Home to the Tasmanian devil, wombats, wallabies, and rare seabirds. Tasmania's wildlife is
					unlike anything else in Australia.
				</p>
			</div>
			<div class="flex flex-col gap-3 rounded-2xl bg-secondary p-6">
				<div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#194139]/10">
					<Plane class="h-6 w-6 text-[#194139]" />
				</div>
				<h3 class="font-heading text-lg font-semibold">Accessible from anywhere in Australia</h3>
				<p class="text-sm leading-6 text-gray-600">
					Short flights from Melbourne and Sydney connect Tasmania to SE Asia's main Australian
					gateway cities in minutes.
				</p>
			</div>
		</div>
	</div>
</div>

<!-- Tour Listings -->
<section
	class="mx-auto flex max-w-7xl flex-col overflow-hidden px-4 pb-12 pt-20 lg:overflow-visible"
>
	<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">
		Popular tours for international visitors
	</p>
	<h2
		class="mt-10 scroll-m-20 pb-8 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		From half-day highlights to full island multi-day adventures
	</h2>
	<Tabs.Root value="dayTours">
		<Tabs.List class="h-full rounded-full px-2 py-1.5">
			<Tabs.Trigger
				class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
				value="dayTours">Day tours</Tabs.Trigger
			>
			<Tabs.Trigger
				class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
				value="multiDayTours">Multi-day tours</Tabs.Trigger
			>
			<Tabs.Trigger
				class="rounded-full font-semibold data-[state=active]:bg-tt-red data-[state=active]:text-background"
				value="privateTours">Private tours</Tabs.Trigger
			>
		</Tabs.List>
		<Tabs.Content value="dayTours">
			<div class="mt-4 w-full">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each sortedDayTours as tour}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<TourCard {tour} />
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
		</Tabs.Content>
		<Tabs.Content value="multiDayTours">
			<div class="mt-4 w-full">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each sortedMultiDayTours as tour}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<TourCard {tour} />
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
		</Tabs.Content>
		<Tabs.Content value="privateTours">
			<div class="mt-4 w-full">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each sortedPrivateTours as tour}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<TourCard {tour} />
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
		</Tabs.Content>
	</Tabs.Root>
</section>

<!-- Private Touring Callout -->
<div class="bg-white py-12 pb-24 sm:py-24">
	<div class="mx-auto max-w-7xl px-4">
		<section
			class="relative mx-auto flex flex-col justify-center overflow-hidden rounded-3xl px-8 py-20 sm:px-12 lg:px-16 lg:py-28"
		>
			<enhanced:img
				alt="Bespoke custom tours Tasmania"
				src="$lib/assets/enhanced/aurora.jpg"
				sizes="(min-width: 1280px) 1280px, 100vw"
				class="absolute inset-0 h-full w-full object-cover lg:object-bottom"
				loading="lazy"
			/>
			<div class="absolute inset-0 bg-foreground/0"></div>

			<div class="relative">
				<h2 class="mt-8 font-heading text-3xl font-bold tracking-tight text-background sm:text-4xl">
					Bespoke custom tours
				</h2>
				<div
					class="mt-6 space-y-4 text-base font-semibold leading-7 text-background/85 lg:space-y-8"
				>
					<p>
						For international visitors planning something beyond the standard itinerary, our bespoke
						custom tours are built entirely around you. Tell us your group size, interests and
						available days, and we will design the experience from scratch.
					</p>

					<div class="hidden lg:contents">
						<p class="mt-4">
							Whether you want a single unforgettable day on Bruny Island, a week taking in Cradle
							Mountain, Wineglass Bay and Port Arthur, or anything in between, we handle every detail.
							Our guides are local, knowledgeable and passionate about showing visitors the real
							Tasmania.
						</p>
						<p class="mt-4">
							Custom tours depart from Hobart or Launceston and suit groups of any size. Wildlife,
							food, wilderness, history or adventure, we tailor the pace and destinations to what
							matters most to you.
						</p>
					</div>

					{#if privateExpanded}
						<div transition:slide={{ duration: 400 }} class="lg:hidden">
							<p>
								Whether you want a single unforgettable day on Bruny Island, a week taking in Cradle
								Mountain, Wineglass Bay and Port Arthur, or anything in between, we handle every
								detail. Our guides are local, knowledgeable and passionate about showing visitors the
								real Tasmania.
							</p>
							<p class="mt-4">
								Custom tours depart from Hobart or Launceston and suit groups of any size. Wildlife,
								food, wilderness, history or adventure, we tailor the pace and destinations to what
								matters most to you.
							</p>
						</div>
					{/if}
				</div>

				<button
					onclick={() => (privateExpanded = !privateExpanded)}
					class="mt-6 flex items-center gap-1 text-sm font-semibold text-background/80 underline-offset-2 transition hover:text-background lg:hidden"
				>
					{privateExpanded ? 'Read less' : 'Read more'}
				</button>

				<div class="mt-8 flex flex-col gap-4 sm:flex-row">
					<Button
						data-sveltekit-noscroll
						onclick={handleAnchorClick}
						href="#enquiry"
						class="pointer-events-auto rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
						>Get in touch</Button
					>
					<Button
						href="/custom-private-tours"
						data-sveltekit-noscroll
						variant="outline"
						class="pointer-events-auto rounded-full border-2 bg-transparent px-6 font-semibold text-background lg:text-lg"
						>More about private tours</Button
					>
				</div>
			</div>
		</section>
	</div>
</div>

<!-- Why Choose Tassie Tours -->
<section class="py-24">
	<div class="mx-auto max-w-7xl px-4">
		<h2 class="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
			Family-owned and locally operated — we've been showing visitors Tasmania since the beginning
		</h2>
		<div class="mx-auto mt-12 grid w-full grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
			{#each whyChooseItems as item}
				<div class="flex flex-col">
					{#if item.illustration === 'mountain'}
						<enhanced:img
							src={mountainIllustration}
							class="mb-4 h-auto w-24"
							alt={item.title}
							sizes="96px"
							loading="lazy"
						/>
					{:else if item.illustration === 'adventure'}
						<enhanced:img
							src={adventureIllustration}
							class="mb-4 h-auto w-24"
							alt={item.title}
							sizes="96px"
							loading="lazy"
						/>
					{:else if item.illustration === 'map'}
						<enhanced:img
							src={mapIllustration}
							class="mb-4 h-auto w-24"
							alt={item.title}
							sizes="96px"
							loading="lazy"
						/>
					{:else if item.illustration === 'certificate'}
						<enhanced:img
							src={certIllustration}
							class="mb-4 h-auto w-24"
							alt={item.title}
							sizes="96px"
							loading="lazy"
						/>
					{:else if item.illustration === 'food'}
						<enhanced:img
							src={foodIllustration}
							class="mb-4 h-auto w-24"
							alt={item.title}
							sizes="96px"
							loading="lazy"
						/>
					{:else if item.illustration === 'resting'}
						<enhanced:img
							src={restingIllustration}
							class="mb-4 h-auto w-24"
							alt={item.title}
							sizes="96px"
							loading="lazy"
						/>
					{/if}
					<h3 class="mb-2 font-heading text-lg font-semibold">{item.title}</h3>
					<p class="text-sm leading-6 text-gray-600">{item.body}</p>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- Iconic Destinations -->
<section class="relative bg-[#194139] pb-32 pt-24">
	<div
		class="absolute -top-48 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-[#194139]"></div>
	</div>
	<div
		class="absolute bottom-0 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<div class="mx-auto max-w-7xl px-4">
		<div class="text-center text-white">
			<h2 class="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				A sample of what Tasmania offers. Every region is extraordinary
			</h2>
		</div>
		<div class="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4">
			{#each destinations as dest}
				<div class="relative h-80 overflow-hidden rounded-2xl">
					{#if dest.image === 'wineglass'}
						<enhanced:img
							src={wineglassImage}
							alt={dest.title}
							class="absolute inset-0 h-full w-full object-cover"
							sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
							loading="lazy"
						/>
					{:else if dest.image === 'bruny'}
						<enhanced:img
							src={brunyDestImage}
							alt={dest.title}
							class="absolute inset-0 h-full w-full object-cover"
							sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
							loading="lazy"
						/>
					{:else if dest.image === 'cradle'}
						<enhanced:img
							src={cradleImage}
							alt={dest.title}
							class="absolute inset-0 h-full w-full object-cover"
							sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
							loading="lazy"
						/>
					{:else if dest.image === 'hobart'}
						<enhanced:img
							src={hobartImage}
							alt={dest.title}
							class="absolute inset-0 h-full w-full object-cover"
							sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
							loading="lazy"
						/>
					{/if}
					<div
						class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"
					></div>
					<div class="absolute bottom-0 p-5">
						<Badge class="bg-[#ffd451] text-foreground hover:bg-[#ffd451]/80">
							<MapPin size={12} />
							<span class="text-xs">{dest.region}</span>
						</Badge>
						<h3 class="mt-2 font-heading text-xl font-semibold text-white">{dest.title}</h3>
						<p class="mt-0.5 text-sm text-white/80">{dest.subtitle}</p>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- Direct Flights Callout -->
<div class="bg-white py-24">
	<div class="mx-auto max-w-5xl px-4 text-center">
		<span
			class="inline-block rounded-full bg-[#ffd451] px-4 py-1.5 text-sm font-semibold text-foreground"
		>
			Coming soon
		</span>
		<h2 class="mt-6 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
			SE Asia to Tasmania — direct flights expected in 12–24 months
		</h2>
		<p class="mx-auto mt-6 max-w-2xl leading-7 text-gray-600">
			Direct flight routes from Southeast Asia to Tasmania are expected within the next 12–24
			months. Now is the time to get familiar with the destination before the crowds arrive.
		</p>
		<div class="mt-8 flex flex-wrap items-center justify-center gap-3">
			{#each cities as city}
				<span
					class="rounded-full border border-[#194139]/20 bg-[#194139]/5 px-5 py-2 text-sm font-medium text-[#194139]"
				>
					{city}
				</span>
			{/each}
		</div>
		<Button
			data-sveltekit-noscroll
			href="#enquiry"
			onclick={handleAnchorClick}
			class="pointer-events-auto mt-10 rounded-full bg-tt-red px-8 font-semibold hover:bg-tt-red/90 lg:text-lg"
		>
			Register your interest
		</Button>
	</div>
</div>

<!-- Social Proof -->
<section class="mx-auto flex max-w-7xl flex-col px-4 pb-20 pt-12 lg:pt-20">
	<h2
		class="scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		Don't take our word for it, hear from our adventurers
	</h2>
	<div class="mx-auto mt-6 w-full max-w-7xl pb-12 lg:mt-12">
		<div class="group col-span-1 flex w-full flex-col">
			<div
				class="relative flex overflow-hidden rounded-2xl bg-gray-900 px-6 pb-32 pt-12 shadow-xl sm:px-10 md:px-12 lg:h-[350px] lg:items-center lg:rounded-3xl lg:px-20 lg:py-20"
			>
				<enhanced:img
					class="absolute inset-0 h-full w-full object-cover"
					src={cradleImage}
					alt="1,000+ five-star reviews"
					loading="lazy"
				/>
				<div class="absolute inset-0 bg-foreground/40 mix-blend-multiply transition-all"></div>
				<div class="relative mx-auto max-w-xl lg:mx-0">
					<h3
						class="text-center font-heading text-2xl font-bold text-background lg:text-left lg:text-4xl"
					>
						2,000+ five-star reviews and counting
					</h3>
				</div>
			</div>
		</div>
		<div class="mx-auto grid-cols-4 lg:grid">
			<div class="col-span-1 hidden flex-col pt-4 lg:flex">
				<TripadvisorAward />
				<enhanced:img
					src="/src/lib/assets/images/tassie-specialist.png"
					alt="Tassie Specialist Award Winner"
					class="ml-8 mt-4 w-36"
					loading="lazy"
				/>
			</div>
			<div class="col-span-3 -mt-24 px-8 lg:px-0 lg:pr-12">
				<Carousel.Root class="w-full" opts={{ align: 'start' }}>
					<Carousel.Content peek>
						{#each reviews as review}
							<Carousel.Item class="basis-[85%] lg:basis-1/3">
								<div class="w-full text-left">
									<Card.Root class="w-full overflow-hidden rounded-2xl border-none shadow-none bg-muted">
										<Card.Header class="relative w-full ">
											<div class="flex gap-0">
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
												<Star class="fill-tt-red text-tt-red" />
											</div>
											<Card.Title class="h-14 line-clamp-2 font-heading text-lg leading-snug"
												>{review.title}</Card.Title
											>
											<Card.Description class="  ">{review.name} — {review.tour}</Card.Description>
										</Card.Header>
										<Card.Content class="grid  gap-4">
											<div class="relative">
												<p class="h-48">{review.body}</p>
												<div
													class="absolute -bottom-6 h-12 w-full bg-gradient-to-t from-muted to-muted/40"
												></div>
											</div>
										</Card.Content>
									</Card.Root>
								</div>
							</Carousel.Item>
						{/each}
					</Carousel.Content>
					<Carousel.Previous class="hidden lg:flex" />
					<Carousel.Next class="hidden lg:flex" />
				</Carousel.Root>
			</div>
		</div>
	</div>
</section>

<!-- Enquiry Form -->
<section class="mx-auto px-4 pb-12 pt-12" id="enquiry">
	<div class="mx-auto max-w-7xl">
		<div class="mx-auto lg:mx-0">
			<h2
				class="text-center font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
			>
				Let's build your Tasmanian trip
			</h2>
			<p class="mx-auto mt-4 max-w-2xl text-center leading-7 text-gray-600">
				Tell us a little about what you're looking for and one of our team will be in touch to help
				you plan the perfect experience.
			</p>
			<div class="mx-auto mt-6 flex max-w-3xl">
				<form
					onsubmit={handleSubmit}
					data-sveltekit-preload-data="off"
					class="relative flex w-full flex-col items-center justify-center gap-10 px-4 pb-12 pt-6"
				>
					<div class="flex w-full flex-col gap-4 sm:flex-row">
						<div class="flex w-full flex-col gap-1.5 sm:w-1/2">
							<Label for="firstName">First name</Label>
							<Input
								required
								type="text"
								id="firstName"
								placeholder="First name"
								bind:value={firstName}
							/>
						</div>
						<div class="flex w-full flex-col gap-1.5 sm:w-1/2">
							<Label for="lastName">Last name</Label>
							<Input
								required
								type="text"
								id="lastName"
								placeholder="Last name"
								bind:value={lastName}
							/>
						</div>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="email">Email address</Label>
						<Input
							required
							type="email"
							id="email"
							placeholder="email@domain.com"
							bind:value={email}
						/>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label>Country of origin</Label>
						<Select.Root type="single" bind:value={country}>
							<Select.Trigger>{countryTrigger}</Select.Trigger>
							<Select.Content>
								<Select.Group>
									{#each countryOptions as option}
										<Select.Item value={option.value} label={option.label}
											>{option.label}</Select.Item
										>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label>Group size</Label>
						<Select.Root type="single" bind:value={groupSize}>
							<Select.Trigger>{groupSizeTrigger}</Select.Trigger>
							<Select.Content>
								<Select.Group>
									{#each groupSizeOptions as option}
										<Select.Item value={option.value} label={option.label}
											>{option.label}</Select.Item
										>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="interests">What interests you?</Label>
						<Textarea
							id="interests"
							placeholder="e.g. nature, wildlife, food, history, adventure..."
							class="min-h-28"
							bind:value={interests}
						/>
					</div>

					<div class="relative mt-6 flex h-32 w-full items-center justify-center">
						{#if !submitted}
							<div
								class="absolute inset-0 flex justify-center"
								in:fly={{ x: -50, duration: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								{#if !loading}
									<Button
										type="submit"
										class="z-50 min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all hover:bg-tt-red/80"
									>
										Send enquiry →
									</Button>
								{:else}
									<Button
										variant="outline"
										disabled
										class="z-50 flex min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all"
									>
										<Loader2 class="mr-2 h-4 w-4 animate-spin" />
									</Button>
								{/if}
							</div>
						{:else}
							<div
								class="absolute inset-0 mx-auto flex w-full max-w-[500px] items-center justify-center"
								in:fly={{ x: 50, duration: 300, delay: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								<Alert.Root onclick={() => (submitted = false)} class="text-left hover:bg-accent">
									<CheckCircle class="h-4 w-4" />
									<Alert.Title>Enquiry received!</Alert.Title>
									<Alert.Description class="text-wrap">
										One of our team will be in touch within 3 business days to help you plan your
										trip.
									</Alert.Description>
								</Alert.Root>
							</div>
						{/if}
					</div>
				</form>
			</div>
		</div>
	</div>
</section>

<!-- Footer CTA -->
<section class="relative bg-[#194139] pb-32 pt-24">
	<div
		class="absolute -top-48 left-0 z-50 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-[#194139]"></div>
	</div>
	<div
		class="absolute bottom-0 left-0 z-50 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<div class="px-6 sm:px-6 lg:px-8">
		<div class="mx-auto max-w-2xl text-center">
			<h2
				class="text-balance font-heading text-4xl font-semibold tracking-tight text-white sm:text-5xl"
			>
				Ready to discover Tasmania?
			</h2>
			<p class="mx-auto mt-6 max-w-xl text-pretty text-lg/8 text-white">
				Browse our full range of tours or get in touch — we'll help you build the perfect Tasmanian
				experience.
			</p>
			<div class="mx-auto mt-8 flex flex-col items-center justify-center gap-4 lg:flex-row">
				<Button
					data-sveltekit-noscroll
					href="/tours"
					class="pointer-events-auto rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
					>View all tours</Button
				>
				<Button
					data-sveltekit-noscroll
					variant="outline"
					href="#enquiry"
					onclick={handleAnchorClick}
					class="pointer-events-auto rounded-full border-2 bg-transparent px-6 font-semibold text-background lg:text-lg"
					>Enquire now</Button
				>
			</div>
		</div>
	</div>
</section>

<style>
	.scaled-video {
		height: 100%;
		width: 100%;
		object-fit: cover;
		animation: slow-scale 15s ease-in-out infinite alternate;
	}
</style>
